import { NgModule, inject, provideAppInitializer } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import { TranslateModule, TranslateLoader, TranslateParser } from "@ngx-translate/core";
import { BrandingTranslateParser } from "./core/branding/branding-translate-parser";
import {
  TranslateHttpLoader,
  TRANSLATE_HTTP_LOADER_CONFIG,
} from "@ngx-translate/http-loader";

import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { HeaderComponent } from "./components/header/header.component";
import { FooterComponent } from "./components/footer/footer.component";
import { AuthService } from "./services/auth.service";
import { LanguageService } from "./services/language.service";
import { AuditCorrelationInterceptor } from "./interceptors/audit-correlation.interceptor";
import { AuditHttpErrorInterceptor } from "./interceptors/audit-http-error.interceptor";
import { AuthInterceptor } from "./interceptors/auth.interceptor";

export function initializeAppLanguage(languageService: LanguageService) {
  return () => firstValueFrom(languageService.initializeLanguage());
}

export function initializeAuthSession(authService: AuthService) {
  return () => {
    if (!authService.isAuthenticated()) {
      return Promise.resolve();
    }
    if (!authService.isTokenExpired() && authService.getToken()) {
      return Promise.resolve();
    }
    return firstValueFrom(authService.ensureValidSession()).then(() => undefined);
  };
}

/** Shell module — same pattern as admin-portal (minimal NgModule + lazy standalone routes). */
@NgModule({
  declarations: [AppComponent, HeaderComponent, FooterComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      fallbackLang: "en",
      loader: {
        provide: TranslateLoader,
        useClass: TranslateHttpLoader,
      },
      parser: {
        provide: TranslateParser,
        useClass: BrandingTranslateParser,
      },
    }),
    AppRoutingModule,
  ],
  providers: [
    AuthService,
    LanguageService,
    provideHttpClient(withInterceptorsFromDi()),
    provideAppInitializer(() => initializeAppLanguage(inject(LanguageService))()),
    provideAppInitializer(() => initializeAuthSession(inject(AuthService))()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuditCorrelationInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuditHttpErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: TRANSLATE_HTTP_LOADER_CONFIG,
      useValue: {
        prefix: "/assets/i18n/",
        suffix: ".json",
      },
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
